<template>
  <div class="fyers-auth">
    <!-- Not connected -->
    <div v-if="!isAuthenticated" class="auth-panel">
      <div class="auth-header">
        <div class="auth-icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <rect width="48" height="48" rx="12" fill="#FF6B35"/>
            <path d="M24 12C17.3726 12 12 17.3726 12 24C12 30.6274 17.3726 36 24 36C30.6274 36 36 30.6274 36 24C36 17.3726 30.6274 12 24 12Z" fill="white"/>
            <path d="M24 18C20.6863 18 18 20.6863 18 24C18 27.3137 20.6863 30 24 30C27.3137 30 30 27.3137 30 24C30 20.6863 27.3137 18 24 18Z" fill="#FF6B35"/>
            <circle cx="21" cy="21" r="2" fill="white"/>
            <circle cx="27" cy="27" r="2" fill="white"/>
          </svg>
        </div>
        <div class="auth-title">
          <h3>Connect Fyers</h3>
          <p class="auth-subtitle">Access NSE, BSE & F&O data with real-time feeds</p>
        </div>
      </div>

      <div class="auth-features">
        <div class="feature-item">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
          </svg>
          <span>Real-time market data</span>
        </div>
        <div class="feature-item">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
          </svg>
          <span>NSE, BSE & F&O segments</span>
        </div>
        <div class="feature-item">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
          </svg>
          <span>Professional charting tools</span>
        </div>
      </div>

      <!-- Referral & Setup Guide -->
      <div class="broker-setup-guide">
        <div class="referral-section">
          <div class="referral-header">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
              <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
            </svg>
            <span>Don't have a Fyers account?</span>
          </div>
          <a
            href="https://signup.fyers.in/?utm-source=AP-Leads&utm-medium=AP4642"
            target="_blank"
            rel="noopener noreferrer"
            class="referral-link"
          >
            Open Fyers Account (Free)
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
            </svg>
          </a>
        </div>

        <button class="setup-guide-toggle" @click="showSetupGuide = !showSetupGuide">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
          </svg>
          {{ showSetupGuide ? 'Hide' : 'Show' }} API Setup Guide
        </button>

        <div v-if="showSetupGuide" class="setup-guide-content">
          <h4>How to get your Fyers API Key (Free)</h4>
          <ol class="setup-steps">
            <li>
              <strong>Create a Fyers Account</strong> — 
              <a href="https://signup.fyers.in/?utm-source=AP-Leads&utm-medium=AP4642" target="_blank" rel="noopener noreferrer">Sign up here</a> 
              if you don't have one. Complete KYC verification.
            </li>
            <li>
              <strong>Go to Fyers API Dashboard</strong> — 
              Visit <a href="https://myapi.fyers.in/" target="_blank" rel="noopener noreferrer">myapi.fyers.in</a> and log in with your Fyers credentials.
            </li>
            <li>
              <strong>Create a New App</strong> — Click "Create App" and fill in:
              <ul class="setup-sub-steps">
                <li><strong>App Name:</strong> Any name (e.g., "TradingCandle")</li>
                <li><strong>Redirect URL:</strong> <code>https://tradingcandle.com/api/fyers/callback</code></li>
                <li><strong>App Type:</strong> Select "Web" or "Desktop"</li>
              </ul>
            </li>
            <li>
              <strong>Get Your Credentials</strong> — After app creation, copy your:
              <ul class="setup-sub-steps">
                <li><strong>App ID</strong> (format: XXXXXXXX-100)</li>
                <li><strong>Secret Key</strong></li>
              </ul>
            </li>
            <li>
              <strong>Connect Below</strong> — Click "Connect with Fyers" and enter your App ID and Secret when prompted.
            </li>
          </ol>
          <p class="setup-note">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
            </svg>
            Fyers API access is free. No trading account balance required for market data.
          </p>
        </div>
      </div>

      <div class="auth-button-group">
        <button
          @click="loginWithFyers"
          :disabled="isLoading"
          class="auth-button"
        >
          <span v-if="isLoading" class="btn-loading">
            <svg class="spinner" width="20" height="20" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="31.416" stroke-dashoffset="31.416">
                <animate attributeName="stroke-dashoffset" dur="0.6s" repeatCount="indefinite" from="31.416" to="0"/>
              </circle>
            </svg>
            Connecting to Fyers...
          </span>
          <span v-else>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 12l2 2 4-4"/>
              <path d="M21 12c-1.5-4.5-6-7.5-12-7.5S1.5 7.5 0 12c1.5 4.5 6 7.5 12 7.5s10.5-3 12-7.5z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            Connect with Fyers
          </span>
        </button>

        <button
          v-if="requiresUserCredentials && hasStoredCredentials"
          @click="showChangeCredentialsModal"
          class="change-credentials-button"
          :disabled="isLoading"
        >
          Change Credentials
        </button>
      </div>

      <div v-if="error" class="error-message">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 16A8 8 0 108 0a8 8 0 000 16zM5.354 4.646a.5.5 0 10-.708.708L7.293 8l-2.647 2.646a.5.5 0 00.708.708L8 8.707l2.646 2.647a.5.5 0 00.708-.708L8.707 8l2.647-2.646a.5.5 0 00-.708-.708L8 7.293 5.354 4.646z"/>
        </svg>
        <span>{{ error }}</span>
      </div>

      <!-- Manual auth code entry -->
      <div v-if="showAuthCodeInput" class="auth-code-section">
        <div class="auth-code-header">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"/>
          </svg>
          <span>Manual Authentication Required</span>
        </div>
        <p class="auth-code-hint">
          After logging in, if you're not redirected automatically, copy the <code>auth_code</code> from the URL and paste it below:
        </p>
        <div class="form-group">
          <label for="authCode">Authorization Code</label>
          <input
            id="authCode"
            v-model="authCode"
            type="text"
            placeholder="Paste auth_code from URL"
            class="form-input"
          />
        </div>
        <div class="modal-actions">
          <button
            @click="completeWithCode"
            :disabled="!authCode || isLoading"
            class="auth-button primary"
          >
            <span v-if="isLoading" class="btn-loading">
              <svg class="spinner" width="16" height="16" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="31.416" stroke-dashoffset="31.416">
                  <animate attributeName="stroke-dashoffset" dur="0.6s" repeatCount="indefinite" from="31.416" to="0"/>
                </circle>
              </svg>
              Completing...
            </span>
            <span v-else>Complete Connection</span>
          </button>
          <button @click="showAuthCodeInput = false" class="cancel-button">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Connected -->
    <div v-else class="auth-success">
      <div class="success-header">
        <div class="success-icon">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <circle cx="32" cy="32" r="32" fill="#10B981"/>
            <path d="M20 32l8 8 16-16" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="success-title">
          <h3>Fyers Connected Successfully</h3>
          <p>Your account is linked and ready to trade</p>
        </div>
      </div>

      <div class="connection-status">
        <div class="status-item">
          <div class="status-dot online"></div>
          <span>Connected</span>
        </div>
        <div class="status-item">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 16s6-5.686 6-10A6 6 0 002 6c0 4.314 6 10 6 10zm0-7a3 3 0 100-6 3 3 0 000 6z"/>
          </svg>
          <span>Real-time data active</span>
        </div>
      </div>

      <div class="user-card" v-if="userProfile">
        <div class="user-avatar">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="20" r="20" fill="#E5E7EB"/>
            <path d="M20 12C16.6863 12 14 14.6863 14 18C14 21.3137 16.6863 24 20 24C23.3137 24 26 21.3137 26 18C26 14.6863 23.3137 12 20 12Z" fill="#9CA3AF"/>
            <path d="M8 32C8 25.3726 13.3726 20 20 20C26.6274 20 32 25.3726 32 32" fill="#9CA3AF"/>
          </svg>
        </div>
        <div class="user-details">
          <h4>{{ userProfile.name || 'Fyers User' }}</h4>
          <div class="user-meta">
            <span class="user-id">ID: {{ userProfile.fy_id || 'N/A' }}</span>
            <span class="user-email">{{ userProfile.email_id || 'N/A' }}</span>
          </div>
        </div>
      </div>

      <div class="auth-actions">
        <button
          @click="loginWithFyers"
          class="auth-button reconnect-action"
          :disabled="isLoading"
        >
          Reconnect
        </button>
        <button
          v-if="requiresUserCredentials && hasStoredCredentials"
          @click="showChangeCredentialsModal"
          class="change-credentials-button"
          :disabled="isLoading"
        >
          Change Credentials
        </button>
        <button @click="disconnect" class="disconnect-button">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M5.5 3.5A.5.5 0 016 4v8a.5.5 0 01-.5.5h-2a.5.5 0 01-.5-.5V4a.5.5 0 01.5-.5h2z"/>
            <path d="M11.5 8a.5.5 0 01.5.5v3a.5.5 0 01-.5.5h-8a.5.5 0 01-.5-.5v-3a.5.5 0 01.5-.5h8z"/>
            <path d="M14 8a2 2 0 11-4 0 2 2 0 014 0z"/>
          </svg>
          Disconnect Account
        </button>
      </div>
    </div>

    <BrokerCredentialsModal
      :show="showCredentialsModal"
      :broker-type="'fyers'"
      :has-stored-credentials="hasStoredCredentials"
      :stored-api-key="storedApiKey"
      :start-in-edit="credentialModalStartsInEdit"
      @close="closeCredentialsModal"
      @save="saveCredentials"
      @use-stored="useStoredCredentials"
    />
  </div>
</template>

<script>
import FyersService from '../services/fyers_service.js';
import DataProviderManager from '../services/data_provider_manager.js';
import { resetBrokerReconnectDismissal } from './BrokerReconnectPopup.vue';
import BrokerCredentialsModal from './BrokerCredentialsModal.vue';
import axios from 'axios';

const API_BASE = process.env.VUE_APP_API_BASE || '';

export default {
  name: 'FyersAuth',
  components: {
    BrokerCredentialsModal
  },
  props: {
    credentialSource: {
      type: String,
      default: null,
      validator: value => value === null || ['env', 'user'].includes(value)
    }
  },
  data() {
    return {
      isLoading: false,
      error: null,
      authCode: '',
      showAuthCodeInput: false,
      isAuthenticated: false,
      userProfile: null,
      showCredentialsModal: false,
      credentialModalStartsInEdit: false,
      credentialModalMode: 'manage',
      hasStoredCredentials: false,
      storedApiKey: '',
      requiresUserCredentials: this.credentialSource === 'user',
      showSetupGuide: false
    };
  },

  async mounted() {
    this.checkAuth();
    this.checkCallbackParams();
    await this.checkStoredCredentials();
  },
  watch: {
    credentialSource(newSource) {
      if (newSource) {
        this.requiresUserCredentials = newSource === 'user';
      }
    }
  },

  methods: {
    checkAuth() {
      this.isAuthenticated = FyersService.isAuthenticated();
      if (this.isAuthenticated) {
        this.loadProfile();
      } else {
        // Try restoring from Clerk metadata
        FyersService.restoreSessionFromServer().then(restored => {
          if (restored) {
            this.isAuthenticated = true;
            this.loadProfile();
          }
        }).catch(() => {});
      }
    },

    /**
     * Check if the current URL has fyers_token from the server callback redirect.
     */
    checkCallbackParams() {
      // No longer needed — we use popup + completeLogin flow now
    },

    async loadProfile() {
      try {
        const profileData = await FyersService.getProfile();
        if (profileData && profileData.s === 'ok' && profileData.data) {
          this.userProfile = profileData.data;
        }
      } catch (error) {
        console.warn('Could not load Fyers profile:', error.message);
      }
    },

    /**
     * Open Fyers login. In Tauri, uses in-app WebView; in web, uses popup.
     * After user logs in, Fyers redirects to our server's /api/fyers/callback
     * which stores the auth code. We then call /api/fyers/complete to exchange
     * the code for an access token stored in Clerk metadata.
     */
    async loginWithFyers(skipCredentialRefresh = false) {
      this.isLoading = true;
      this.error = null;

      try {
        const credentialSource = await this.fetchCredentialSource();
        if (credentialSource) {
          this.requiresUserCredentials = credentialSource === 'user';
        }

        let credentialDetails = null;
        if (!skipCredentialRefresh) {
          credentialDetails = await this.checkStoredCredentials({ preserveOnError: true });
        }

        const hasCredentials = credentialDetails ? credentialDetails.hasCredentials : this.hasStoredCredentials;
        if (this.requiresUserCredentials && !hasCredentials) {
          this.credentialModalMode = 'connect';
          this.credentialModalStartsInEdit = false;
          this.showCredentialsModal = true;
          this.isLoading = false;
          return;
        }

        const authUrl = await FyersService.getAuthUrl();

        // Check if running in Tauri
        if (window.__TAURI__) {
          // Tauri environment - open OAuth in an in-app WebView window
          await this.openTauriOAuthWindow(authUrl);
          return;
        }

        // Web environment - open in popup
        const popup = window.open(authUrl, 'fyers_login', 'width=600,height=700');

        // Poll for the popup to close (user finished login)
        const pollInterval = setInterval(async () => {
          try {
            if (!popup || popup.closed) {
              clearInterval(pollInterval);

              // Small delay to ensure the callback has been processed
              await new Promise(resolve => setTimeout(resolve, 1500));

              // Popup closed — complete the login via the server.
              // Server exchanges the auth code for a token and stores it in Clerk.
              try {
                const result = await FyersService.completeLogin();
                this.isAuthenticated = true;
                await this.loadProfile();
                DataProviderManager.switchProvider('fyers');
                this.$emit('authenticated', result);
                FyersService.loadSymbolMaster('NSE_CM').catch(() => {});
                resetBrokerReconnectDismissal();
              } catch (completeErr) {
                this.error = completeErr.response?.data?.error || completeErr.message;
                this.showAuthCodeInput = true;
              }
              this.isLoading = false;
            }
          } catch (e) {
            // Cross-origin access error is expected while popup is on Fyers domain
          }
        }, 1000);

        // Safety timeout — stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (this.isLoading) {
            this.isLoading = false;
            this.error = 'Login timed out. Please try again.';
          }
        }, 300000);

      } catch (error) {
        if (error.response?.data?.credentialSource === 'user') {
          this.requiresUserCredentials = true;
          this.hasStoredCredentials = false;
          this.credentialModalMode = 'connect';
          this.credentialModalStartsInEdit = false;
          this.showCredentialsModal = true;
          this.error = null;
          this.isLoading = false;
          return;
        }

        this.error = 'Failed to connect: ' + (error.response?.data?.error || error.message);
        this.isLoading = false;
      }
    },

    /**
     * Open OAuth in a Tauri WebviewWindow (in-app browser).
     * Monitors URL changes to detect when the OAuth flow completes.
     */
    async openTauriOAuthWindow(authUrl) {
      try {
        // Dynamically import Tauri API
        const { WebviewWindow } = await import('@tauri-apps/api/webviewWindow');

        // Create a new webview window for OAuth
        const oauthWindow = new WebviewWindow('fyers-oauth', {
          url: authUrl,
          title: 'Fyers Login',
          width: 600,
          height: 700,
          center: true,
          resizable: true,
          decorations: true
        });

        // Listen for window close event
        let isCompleted = false;
        let checkInterval = null;

        oauthWindow.once('tauri://destroyed', async () => {
          if (!isCompleted) {
            // Window closed without completion - try to complete anyway
            // (callback might have been processed)
            clearInterval(checkInterval);
            await this.completeOAuthFlow();
          }
        });

        // Poll to check auth status while window is open
        checkInterval = setInterval(async () => {
          try {
            const status = await FyersService.checkAuthStatus();
            if (status.success) {
              // Auth completed - close window and finish
              isCompleted = true;
              clearInterval(checkInterval);
              oauthWindow.close();
              await this.completeOAuthFlow();
            }
          } catch (e) {
            // Ignore errors during polling
          }
        }, 2000);

        // Safety timeout - stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!isCompleted && this.isLoading) {
            oauthWindow.close();
            this.isLoading = false;
            this.error = 'Login timed out. Please try again.';
          }
        }, 300000);

      } catch (error) {
        console.error('Failed to open Tauri OAuth window:', error);
        // Fallback to system browser if WebviewWindow fails
        window.open(authUrl, '_blank');
        // Start polling for completion
        this.pollForTauriAuthCompletion();
      }
    },

    /**
     * Complete the OAuth flow after user authentication.
     * Called after the OAuth window closes or callback is detected.
     */
    async completeOAuthFlow() {
      try {
        const result = await FyersService.completeLogin();
        this.isAuthenticated = true;
        await this.loadProfile();
        DataProviderManager.switchProvider('fyers');
        this.$emit('authenticated', result);
        FyersService.loadSymbolMaster('NSE_CM').catch(() => {});
        resetBrokerReconnectDismissal();
      } catch (completeErr) {
        this.error = completeErr.response?.data?.error || completeErr.message;
        this.showAuthCodeInput = true;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Fallback polling method for when using system browser in Tauri.
     */
    async pollForTauriAuthCompletion() {
      const maxAttempts = 150; // 5 minutes at 2 second intervals
      let attempts = 0;

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const status = await FyersService.checkAuthStatus();

          if (status.success) {
            clearInterval(pollInterval);
            this.isAuthenticated = true;
            await this.loadProfile();
            DataProviderManager.switchProvider('fyers');
            this.$emit('authenticated', status);
            FyersService.loadSymbolMaster('NSE_CM').catch(() => {});
            resetBrokerReconnectDismissal();
            this.isLoading = false;
            return;
          }

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            this.isLoading = false;
            this.error = 'Login timed out. Please try again.';
          }
        } catch (error) {
          // Continue polling on error
        }
      }, 2000);
    },

    /**
     * Fallback: user pastes auth_code manually (shouldn't normally be needed).
     */
    async completeWithCode() {
      this.isLoading = true;
      this.error = null;

      try {
        // The authCode here is actually the state/sessionId
        const result = await FyersService.completeLogin(this.authCode);
        if (result.success) {
          this.isAuthenticated = true;
          this.showAuthCodeInput = false;
          await this.loadProfile();
          DataProviderManager.switchProvider('fyers');
          this.$emit('authenticated', result);
          FyersService.loadSymbolMaster('NSE_CM').catch(() => {});
          resetBrokerReconnectDismissal();
        }
      } catch (error) {
        this.error = 'Authentication failed: ' + (error.response?.data?.error || error.message);
      } finally {
        this.isLoading = false;
      }
    },

    disconnect() {
      FyersService.disconnectFromServer();
      this.isAuthenticated = false;
      this.userProfile = null;
      DataProviderManager.switchProvider('binance');
      this.$emit('disconnected');
    },

    async checkStoredCredentials(options = {}) {
      try {
        const token = await this._getClerkToken();
        const response = await axios.get(API_BASE + '/api/broker-credentials/fyers/details', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        this.hasStoredCredentials = response.data.hasCredentials;
        this.storedApiKey = response.data.apiKey || '';
        this.requiresUserCredentials = response.data.requiresUserCredentials === true;
        return response.data;
      } catch (error) {
        console.warn('[Fyers] Could not check stored credentials:', error.message);
        if (!options.preserveOnError) {
          this.hasStoredCredentials = false;
          this.storedApiKey = '';
          this.requiresUserCredentials = this.credentialSource ? this.credentialSource === 'user' : true;
        }
        return null;
      }
    },

    async fetchCredentialSource() {
      try {
        const response = await axios.get((API_BASE || window.location.origin) + '/api/integrations/status');
        const setting = response.data?.integrations?.fyers;
        return setting && setting.credentialSource ? setting.credentialSource : null;
      } catch (error) {
        console.warn('[Fyers] Could not fetch credential source:', error.message);
        return null;
      }
    },

    async saveCredentials({ credentials }, controls) {
      try {
        const token = await this._getClerkToken();
        await axios.post(API_BASE + '/api/broker-credentials/save', {
          brokerType: 'fyers',
          credentials
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        this.hasStoredCredentials = true;
        this.storedApiKey = credentials.apiKey;
        this.error = null;
        this.showCredentialsModal = false;
        this.credentialModalStartsInEdit = false;

        if (controls && controls.resolve) controls.resolve();

        this.$emit('credentials-saved', 'fyers');

        if (this.credentialModalMode === 'connect') {
          this.$nextTick(() => {
            this.loginWithFyers(true);
          });
        }
      } catch (error) {
        const message = error.response?.data?.error || 'Failed to save credentials';
        if (controls && controls.reject) {
          controls.reject(new Error(message));
          return;
        }
        throw new Error(message);
      }
    },

    useStoredCredentials() {
      this.loginWithFyers();
    },

    showChangeCredentialsModal() {
      this.credentialModalMode = 'manage';
      this.credentialModalStartsInEdit = this.hasStoredCredentials;
      this.showCredentialsModal = true;
    },

    closeCredentialsModal() {
      this.showCredentialsModal = false;
      this.credentialModalStartsInEdit = false;
      this.credentialModalMode = 'manage';
    },

    async _getClerkToken() {
      try {
        if (window.__vue_clerk && window.__vue_clerk.getToken) {
          return await window.__vue_clerk.getToken();
        }
        if (window.Clerk && window.Clerk.session) {
          return await window.Clerk.session.getToken();
        }
        if (window.Clerk && window.Clerk.getToken) {
          return await window.Clerk.getToken();
        }
      } catch (e) {
        console.warn('[Fyers] Could not get Clerk token:', e.message);
      }
      return null;
    }
  }
};
</script>

<style scoped>
/* Theme-aware via global CSS variables defined in App.vue (data-theme="dark"). */

.fyers-auth {
  max-width: 500px;
  margin: 0 auto;
  padding: 24px;
  color: var(--tc-text-primary);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Auth Panel Styles */
.auth-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
}

.auth-icon {
  flex-shrink: 0;
}

.auth-title h3 {
  margin: 0 0 4px;
  font-size: 24px;
  font-weight: 700;
  color: var(--tc-text-primary);
  line-height: 1.2;
}

.auth-subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--tc-text-secondary);
  line-height: 1.4;
}

.auth-features {
  display: grid;
  gap: 12px;
  margin-bottom: 24px;
  padding: 20px;
  background: var(--tc-bg-secondary);
  border-radius: 12px;
  border: 1px solid var(--tc-border-primary);
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: var(--tc-text-primary);
}

.feature-item svg {
  flex-shrink: 0;
  color: var(--tc-green);
}

/* Button Styles */
.auth-button-group {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.auth-button {
  flex: 1;
  padding: 14px 20px;
  background: linear-gradient(135deg, #FF6B35 0%, #F97316 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 6px -1px rgba(255, 107, 53, 0.1), 0 2px 4px -1px rgba(255, 107, 53, 0.06);
}

.change-credentials-button {
  padding: 14px 18px;
  background: var(--tc-bg-secondary);
  color: var(--tc-text-primary);
  border: 1px solid var(--tc-border-primary);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.change-credentials-button:hover:not(:disabled) {
  background: var(--tc-bg-hover);
  border-color: var(--tc-border-hover);
}

.change-credentials-button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.auth-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(255, 107, 53, 0.1), 0 4px 6px -2px rgba(255, 107, 53, 0.05);
}

.auth-button:active:not(:disabled) {
  transform: translateY(0);
}

.auth-button:disabled {
  background: var(--tc-bg-tertiary);
  color: var(--tc-text-tertiary);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
  opacity: 0.6;
}

.auth-button.primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.1), 0 2px 4px -1px rgba(59, 130, 246, 0.06);
}

.auth-button.primary:hover:not(:disabled) {
  box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.1), 0 4px 6px -2px rgba(59, 130, 246, 0.05);
}

.btn-loading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Error Message */
.error-message {
  margin-top: 16px;
  padding: 12px 16px;
  background: var(--tc-red-bg);
  color: var(--tc-red);
  border: 1px solid var(--tc-red);
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Auth Code Section */
.auth-code-section {
  margin-top: 20px;
  padding: 20px;
  background: var(--tc-bg-secondary);
  border: 1px solid var(--tc-border-primary);
  border-radius: 12px;
}

.auth-code-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.auth-code-hint {
  font-size: 14px;
  color: var(--tc-text-secondary);
  margin: 0 0 16px;
  line-height: 1.5;
}

.auth-code-hint code {
  background: var(--tc-bg-tertiary);
  color: var(--tc-text-primary);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 13px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--tc-text-primary);
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  background: var(--tc-input-bg);
  color: var(--tc-input-text);
  border: 2px solid var(--tc-input-border);
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  transition: all 0.2s ease;
}

.form-input::placeholder {
  color: var(--tc-input-placeholder);
}

.form-input:focus {
  outline: none;
  border-color: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.15);
}

.modal-actions {
  display: flex;
  gap: 12px;
}

.modal-actions .auth-button {
  flex: 2;
  padding: 12px;
  font-size: 14px;
}

.cancel-button {
  flex: 1;
  padding: 12px;
  background: var(--tc-bg-secondary);
  color: var(--tc-text-secondary);
  border: 2px solid var(--tc-border-primary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.cancel-button:hover {
  background: var(--tc-bg-hover);
  border-color: var(--tc-border-hover);
  color: var(--tc-text-primary);
}

/* Success State */
.auth-success {
  text-align: center;
}

.success-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
}

.success-title h3 {
  margin: 16px 0 4px;
  font-size: 20px;
  font-weight: 700;
  color: var(--tc-green);
}

.success-title p {
  margin: 0;
  font-size: 14px;
  color: var(--tc-text-secondary);
}

.connection-status {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-bottom: 24px;
  padding: 16px;
  background: var(--tc-green-bg);
  border-radius: 12px;
  border: 1px solid var(--tc-green);
}

.status-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--tc-green);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  position: relative;
}

.status-dot.online::after {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 50%;
  border: 2px solid #10b981;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.user-card {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding: 20px;
  background: var(--tc-card-bg);
  border-radius: 12px;
  border: 1px solid var(--tc-card-border);
  box-shadow: var(--tc-shadow-sm);
}

.user-avatar {
  flex-shrink: 0;
}

.user-details h4 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.user-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-id, .user-email {
  font-size: 13px;
  color: var(--tc-text-secondary);
}

.auth-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.auth-actions .reconnect-action {
  flex: 0 1 auto;
  box-shadow: none;
}

.disconnect-button {
  padding: 12px 24px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 6px -1px rgba(239, 68, 68, 0.1), 0 2px 4px -1px rgba(239, 68, 68, 0.06);
}

.disconnect-button:hover {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 10px 15px -3px rgba(239, 68, 68, 0.1), 0 4px 6px -2px rgba(239, 68, 68, 0.05);
}

/* Dark/light theming handled by global CSS variables on [data-theme="dark"] in App.vue. */

/* Broker Setup Guide & Referral Styles */
.broker-setup-guide {
  margin-bottom: 20px;
  padding: 16px;
  background: var(--tc-bg-secondary);
  border: 1px solid var(--tc-border-primary);
  border-radius: 12px;
}

.referral-section {
  margin-bottom: 12px;
}

.referral-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--tc-text-secondary);
  margin-bottom: 8px;
}

.referral-header svg {
  flex-shrink: 0;
  color: var(--tc-accent);
}

.referral-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: var(--tc-accent-bg);
  color: var(--tc-accent);
  border: 1px solid var(--tc-accent);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
}

.referral-link:hover {
  background: var(--tc-accent);
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.setup-guide-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: none;
  border: 1px solid var(--tc-border-primary);
  border-radius: 8px;
  color: var(--tc-text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
}

.setup-guide-toggle:hover {
  background: var(--tc-bg-hover);
  color: var(--tc-text-primary);
  border-color: var(--tc-border-hover);
}

.setup-guide-toggle svg {
  flex-shrink: 0;
  color: var(--tc-accent);
}

.setup-guide-content {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--tc-border-primary);
}

.setup-guide-content h4 {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.setup-steps {
  margin: 0 0 16px;
  padding-left: 20px;
  list-style: decimal;
}

.setup-steps li {
  margin-bottom: 12px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--tc-text-primary);
}

.setup-steps li a {
  color: var(--tc-accent);
  text-decoration: none;
  font-weight: 500;
}

.setup-steps li a:hover {
  text-decoration: underline;
}

.setup-sub-steps {
  margin: 8px 0 0;
  padding-left: 18px;
  list-style: disc;
}

.setup-sub-steps li {
  margin-bottom: 4px;
  font-size: 12px;
  color: var(--tc-text-secondary);
}

.setup-sub-steps li code {
  background: var(--tc-bg-tertiary);
  color: var(--tc-text-primary);
  padding: 1px 5px;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 11px;
}

.setup-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  background: var(--tc-accent-bg);
  border-radius: 8px;
  font-size: 12px;
  color: var(--tc-accent);
  line-height: 1.5;
}

.setup-note svg {
  flex-shrink: 0;
  margin-top: 1px;
}
</style>
